"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _lodash = _interopRequireDefault(require("lodash"));
var _axios = _interopRequireDefault(require("axios"));
var _basic = _interopRequireDefault(require("./_basic"));
/**
 * @typedef {'learned'|'list'|'prebuilt'|'regex'} CompositeEntityType
 */

/**
 * @typedef {'separateComponents'|'combineComponents'} CompositionSetting
 */

/**
 * @typedef {Object} CompositeEntity
 * @property {string} id
 * @property {string} kbId
 * @property {string} versionId
 * @property {string} name
 * @property {CompositeEntityType} type
 * @property {CompositionSetting} compositionSetting
 * @property {Array.<CompositeEntityType>} requiredComponents
 * @property {CompositeEntityList[]} [list]
 * @property {CompositeEntityPrebuilt[]} [prebuilts]
 * @property {CompositeEntityRegex[]} [regex]
 */

/**
 * @typedef {Omit<CompositeEntity, 'id' | 'versionId'>} CompositeEntityCreateProps
 */

/**
 * @typedef {Omit<CompositeEntity, 'versionId'>} CompositeEntityUpdateProps
 */

/**
 * @typedef {Object} CompositeEntityList
 * @property {string} name
 * @property {CompositeEntityListSynonyms[]} synonyms
 */

/**
 * @typedef {Object} CompositeEntityListSynonyms
 * @property {string} language
 * @property {string[]} values
 */

/**
 * @typedef {string} CompositeEntityPrebuilt
 */

/**
 * @typedef {Object} CompositeEntityRegex
 * @property {string} name
 * @property {string} language
 * @property {string} regexPattern
 */

/**
 * @typedef {Object} CompositeEntityDeleteResult
 * @property {boolean} success
 */

/**
 * @typedef {Object} UtteranceCreateResult
 * @property {number} appCreated
 * @property {Object} application
 * @property {string} application.name
 * @property {string} application.id
 * @property {boolean} autoGenerated
 * @property {string} culture
 * @property {number} dateCreated
 * @property {Object[]} entities
 * @property {Object[]} entityPredictions
 * @property {string} id
 * @property {Object} intent
 * @property {string} intent.name
 * @property {string} intent.label
 * @property {string} intent.id
 * @property {string} intent.type
 * @property {number} intentCreated
 * @property {string} kbId
 * @property {string} text
 * @property {boolean} unrecognized
 * @property {string | null} versionId
 */

/**
 * @typedef {Object} UtterancesBulkCreateResult
 * @property {number} appCreated
 * @property {string} application
 * @property {boolean} autoGenerated
 * @property {string} culture
 * @property {number} dateCreated
 * @property {Object[]} entities
 * @property {Object[]} entityPredictions
 * @property {string} id
 * @property {string} intent
 * @property {number} intentCreated
 * @property {string} kbId
 * @property {string} text
 * @property {boolean} unrecognized
 * @property {string | null} versionId
 */

/**
 * @typedef {Object} UtteranceUpdateResult
 * @property {string} accountId
 * @property {number} appCreated
 * @property {Object} application
 * @property {string} application.id
 * @property {string} application.name
 * @property {boolean} autoGenerated
 * @property {string} culture
 * @property {number} dateCreated
 * @property {Object[]} entities
 * @property {Object[]} entityPredictions
 * @property {string} id
 * @property {Object} intent
 * @property {string} intent.id
 * @property {string} intent.label
 * @property {string} intent.name
 * @property {string} intent.text
 * @property {number} intentCreated
 * @property {string} kbId
 * @property {string} text
 * @property {boolean} unrecognized
 * @property {string | null} versionId
 */

/**
 * @typedef {Object} NluAppLimits
 * @property {Object} common
 * @property {number} common.KB__NAME__MAX_LENGTH
 * @property {number} common.KB__DESCRIPTION__MAX_LENGTH
 * @property {number} common.CONTEXTS__NAME__MAX_LENGTH
 * @property {number} common.INTENTS__LABEL__MIN_LENGTH
 * @property {number} common.INTENTS__LABEL__MAX_LENGTH
 * @property {number} common.INTENTS__JSON_NAME__MAX_LENGTH
 * @property {number} common.INTENTS__TEXT_NAME__MAX_LENGTH
 * @property {number} common.UTTERANCES__TEXT__MAX_LENGTH
 * @property {number} common.UTTERANCES__MAX_PER_BULK
 * @property {number} common.VERSIONS__NAME__MAX_LENGTH
 * @property {number} common.ENTITIES__NAME__MAX_LENGTH
 * @property {number} common.FLOWS__PARAMETERS__MAX_SIZE
 * @property {number} common.POSTPROCESS_FLOW__NAME__MAX_LENGTH
 * @property {Object} base
 * @property {number} base.KB__MAX_PER_ACCOUNT
 * @property {number} base.CONTEXTS__MIN_PER_KB
 * @property {number} base.CONTEXTS__MAX_PER_KB
 * @property {number} base.UTTERANCES__MIN_PER_KB
 * @property {number} base.UTTERANCES__MIN_PER_INTENT
 * @property {number} base.UTTERANCES__MAX_PER_KB
 * @property {number} base.INTENTS__MIN_PER_CONTEXT
 * @property {number} base.INTENTS__MIN_PER_KB
 * @property {number} base.INTENTS__MAX_PER_KB
 * @property {number} base.VERSIONS__MAX_PER_KB
 * @property {Object} clu
 * @property {number} clu.KB__MAX_PER_ACCOUNT
 * @property {number} clu.CONTEXTS__MIN_PER_KB
 * @property {number} clu.CONTEXTS__MAX_PER_KB
 * @property {number} clu.UTTERANCES__MIN_PER_KB
 * @property {number} clu.UTTERANCES__MIN_PER_INTENT
 * @property {number} clu.UTTERANCES__MAX_PER_KB
 * @property {number} clu.INTENTS__MIN_PER_CONTEXT
 * @property {number} clu.INTENTS__MIN_PER_KB
 * @property {number} clu.INTENTS__MAX_PER_KB
 * @property {number} clu.VERSIONS__MAX_PER_KB
 * @property {number} clu.ENTITIES__COMPONENT__NAME__MAX_LENGTH
 * @property {number} clu.ENTITIES__MAX_PER_KB
 * @property {number} clu.ENTITIES__LIST__MAX_SYNONYMS_PER_ENTITY
 * @property {number} clu.ENTITIES__LIST__MAX_SYNONYMS_PER_KB
 * @property {number} clu.ENTITIES__LIST__SYNONYM__MAX_LENGTH
 * @property {number} clu.ENTITIES__PREBUILT__COMPONENTS__MAX_PER_ENTITY
 * @property {number} clu.ENTITIES__REGEX__MAX_PER_KB
 * @property {number} clu.ENTITIES__REGEX__PATTERN__MAX_LENGTH
 * @property {Object} luis
 * @property {number} luis.KB__MAX_PER_ACCOUNT
 * @property {number} luis.CONTEXTS__MIN_PER_KB
 * @property {number} luis.CONTEXTS__MAX_PER_KB
 * @property {number} luis.UTTERANCES__MIN_PER_KB
 * @property {number} luis.UTTERANCES__MIN_PER_INTENT
 * @property {number} luis.UTTERANCES__MAX_PER_KB
 * @property {number} luis.INTENTS__MIN_PER_CONTEXT
 * @property {number} luis.INTENTS__MIN_PER_KB
 * @property {number} luis.INTENTS__MAX_PER_KB
 * @property {number} luis.VERSIONS__MAX_PER_KB
 * @property {number} luis.ENTITIES__ROLE__MAX_LENGTH
 * @property {number} luis.ENTITIES__ROLES__MAX_PER_ENTITY
 * @property {number} luis.ENTITIES__ROLES__MAX_PER_CONTEXT
 * @property {number} luis.ENTITIES__LIST__MAX_PER_KB
 * @property {number} luis.ENTITIES__LIST__MAX_CHILDREN_PER_KB
 * @property {number} luis.ENTITIES__LIST__CANONICAL_FORM__MAX_LENGTH
 * @property {number} luis.ENTITIES__ML__MAX_PER_KM
 * @property {number} luis.ENTITIES__ML__SUB_ENTITIES__MAX_PER_ENTITY
 * @property {number} luis.ENTITIES__ML__SUB_ENTITIES__MAX_PER_KM
 * @property {number} luis.ENTITIES__ML__SUB_ENTITIES__CHILDREN__MAX_LENGTH
 * @property {number} luis.ENTITIES__ML__SUB_ENTITIES__CHILDREN__MAX_DEPTH
 * @property {number} luis.ENTITIES__ML__SUB_FEATURES__MAX_PER_ENTITY
 * @property {number} luis.ENTITIES__REGEX__MAX_PER_KB
 * @property {number} luis.ENTITIES__REGEX__PATTERN__MAX_LENGTH
 * @property {number} luis.INTENTS__SUB_ENTITIES__MAX_SIZE
 * @property {number} luis.INTENTS__SUB_FEATURES__MAX_SIZE
 * @property {number} luis.PATTERNS__MAX_PER_KB
 * @property {number} luis.PATTERNS__PATTERN_ANY_ENTITIES
 * @property {number} luis.PHRASE_LISTS__MAX_PER_KB
 * @property {number} luis.PHRASE_LISTS__GLOBAL__MAX_PER_ACCOUNT
 * @property {number} luis.PHRASE_LISTS__NAME__MAX_LENGTH
 * @property {Object} rasa
 * @property {number} rasa.KB__MAX_PER_ACCOUNT
 * @property {number} rasa.CONTEXTS__MIN_PER_KB
 * @property {number} rasa.CONTEXTS__MAX_PER_KB
 * @property {number} rasa.UTTERANCES__MIN_PER_KB
 * @property {number} rasa.UTTERANCES__MIN_PER_INTENT
 * @property {number} rasa.UTTERANCES__MAX_PER_KB
 * @property {number} rasa.INTENTS__MIN_PER_CONTEXT
 * @property {number} rasa.INTENTS__MIN_PER_KB
 * @property {number} rasa.INTENTS__MAX_PER_KB
 * @property {number} rasa.VERSIONS__MAX_PER_KB
 */

/**
 * @typedef {Object} NluResponse<T>
 * @property {Array<T>} items
 * @property {string} last
 * @property {Object} total
 * @property {number} total.value
 * @property {string} total.relation
 */

class NluApi extends _basic.default {
  /**
   * @constructor
   */
  constructor(_ref) {
    let {
      userTokenFactory,
      fingerPrintTokenFactory,
      nluApiUrl
    } = _ref;
    super({
      userTokenFactory,
      fingerPrintTokenFactory
    });
    this.nluApiUrl = nluApiUrl;
  }
  async _paginateOverList(callList, params) {
    const queryParams = _lodash.default.clone(params);
    let last = null;
    let totalItems = [];
    let result;
    do {
      if (last) {
        queryParams.last = last;
      }
      result = await callList.call(this, queryParams);
      last = result.last;
      totalItems = _lodash.default.chain(totalItems).concat(result.items).value();
    } while (last && !_lodash.default.isEmpty(result.items));
    return totalItems;
  }
  async _paginateOverListWithTotal(callList, params) {
    const queryParams = _lodash.default.clone(params);
    let last = null;
    let totalItems = [];
    let maxItemsNumber = 0;
    do {
      if (last) {
        queryParams.last = last;
      }
      const result = await callList.call(this, queryParams);
      last = result.last;
      if (!maxItemsNumber) maxItemsNumber = result.total.value;
      totalItems = totalItems.concat(result.items);
    } while (last && totalItems.length < maxItemsNumber);
    return totalItems;
  }
  async indexLimits() {
    const headers = await this.getHeaders();
    return _axios.default.get(`${this.nluApiUrl}/administration/limits/index`, headers).then(response => response.data);
  }

  /**
   *
   * @param {string} path
   * @param {string} scope - use `legacy` value instead of the default `all` to get the old structure of limits
   * @returns {Promise<NluAppLimits>}
   */
  async getLimits() {
    let path = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 'ALL_LIMITS';
    let scope = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'all';
    const headers = await this.getHeaders();
    return _axios.default.get(`${this.nluApiUrl}/administration/limits`, {
      ...headers,
      params: {
        path,
        scope
      }
    }).then(response => response.data);
  }
  async postLimits(_ref2) {
    let {
      accountId,
      limits
    } = _ref2;
    const headers = await this.getHeaders();
    return _axios.default.post(`${this.nluApiUrl}/administration/limits`, {
      accountId,
      limits
    }, headers).then(response => response.data);
  }
  async listAllPrebuiltEntities() {
    let {
      language,
      kbId
    } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    const headers = await this.getHeaders();
    return _axios.default.get(`${this.nluApiUrl}/kb/entities/prebuilt/all`, {
      ...headers,
      params: {
        language,
        kbId
      }
    }).then(response => response.data);
  }
  async listEntities() {
    let {
      type,
      kbId,
      versionId,
      last,
      limit
    } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    const headers = await this.getHeaders();
    return _axios.default.get(`${this.nluApiUrl}/kb/entities/${type}`, {
      ...headers,
      params: {
        kbId,
        versionId,
        last,
        limit
      }
    }).then(response => response.data);
  }
  async listFeatures() {
    let {
      kbId,
      versionId,
      last,
      limit
    } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    const headers = await this.getHeaders();
    return _axios.default.get(`${this.nluApiUrl}/phraselists`, {
      ...headers,
      params: {
        kbId,
        versionId,
        last,
        limit
      }
    }).then(response => response.data);
  }
  async listAllEntities() {
    let {
      type,
      versionId,
      kbId,
      limit
    } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    return this._paginateOverList(this.listEntities, {
      type,
      kbId,
      versionId,
      limit
    });
  }
  async listAllLearnedEntities() {
    let {
      type,
      versionId,
      kbId,
      limit,
      last
    } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    return this._paginateOverListWithTotal(this.listCompositeEntities, {
      type,
      versionId,
      kbId,
      limit,
      last
    });
  }
  async listAllFeatures() {
    let {
      kbId,
      versionId,
      limit
    } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    return this._paginateOverList(this.listFeatures, {
      kbId,
      versionId,
      limit
    });
  }
  async listAllIntents() {
    let {
      kbId,
      appId,
      versionId,
      name,
      label,
      limit
    } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    return this._paginateOverList(this.listIntents, {
      kbId,
      appId,
      versionId,
      name,
      label,
      limit
    });
  }
  async getKnowledgeModelById(_ref3) {
    let {
      kbId
    } = _ref3;
    const headers = await this.getHeaders();
    return _axios.default.get(`${this.nluApiUrl}/knowledge-base/${kbId}`, {
      ...headers
    }).then(response => response.data);
  }
  async listKnowledgeBases() {
    let {
      last,
      limit
    } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    const headers = await this.getHeaders();
    return _axios.default.get(`${this.nluApiUrl}/knowledge-base`, {
      ...headers,
      params: {
        last,
        limit
      }
    }).then(response => response.data);
  }
  async listAllKnowledgeBases() {
    let {
      limit
    } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    return this._paginateOverList(this.listKnowledgeBases, {
      limit
    });
  }
  async listApplications() {
    let {
      kbId,
      text,
      pattern,
      versionId,
      last,
      limit,
      projection
    } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    const headers = await this.getHeaders();
    return _axios.default.get(`${this.nluApiUrl}/applications`, {
      ...headers,
      params: {
        kbId,
        last,
        limit,
        text,
        pattern,
        versionId,
        projection
      }
    }).then(response => response.data);
  }
  async listAllApplications() {
    let {
      kbId,
      text,
      pattern,
      versionId,
      limit,
      projection
    } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    return this._paginateOverList(this.listApplications, {
      kbId,
      text,
      pattern,
      versionId,
      limit,
      projection
    });
  }
  async listIntentsLabels(_ref4) {
    let {
      appId
    } = _ref4;
    const headers = await this.getHeaders();
    return _axios.default.get(`${this.nluApiUrl}/intents/labels`, {
      ...headers,
      params: {
        appId
      }
    }).then(response => response.data);
  }
  async listUtterances(_ref5) {
    let {
      kbId,
      last,
      limit,
      ...query
    } = _ref5;
    const headers = await this.getHeaders();
    return _axios.default.get(`${this.nluApiUrl}/utterances`, {
      ...headers,
      params: {
        kbId,
        last,
        limit,
        ...query
      }
    }).then(response => response.data);
  }

  /**
   * Get suggested utterances
   *
   * @param {Object} params
   * @param {string} params.kbId
   * @param {string} params.contextId
   * @param {string} params.utteranceId
   * @param {number} [params.count]
   *
   * @returns {Promise<string[]>}
   */
  async getSuggestedUtterances(_ref6) {
    let {
      kbId,
      contextId,
      utteranceId,
      count
    } = _ref6;
    const headers = await this.getHeaders();
    return _axios.default.get(`${this.nluApiUrl}/knowledge-bases/${kbId}/contexts/${contextId}/utterances/${utteranceId}/suggest-utterances`, {
      ...headers,
      params: {
        count
      }
    }).then(response => response.data);
  }
  async getUtterancesQty(_ref7) {
    let {
      kbId
    } = _ref7;
    const headers = await this.getHeaders();
    return _axios.default.get(`${this.nluApiUrl}/knowledge-bases/${kbId}/utterances/count`, {
      ...headers
    }).then(response => response.data);
  }
  async listPatterns(_ref8) {
    let {
      kbId,
      last,
      limit,
      ...query
    } = _ref8;
    const headers = await this.getHeaders();
    return _axios.default.get(`${this.nluApiUrl}/patterns`, {
      ...headers,
      params: {
        kbId,
        last,
        limit,
        ...query
      }
    }).then(response => response.data);
  }
  async listAllUtterances(_ref9) {
    let {
      kbId,
      limit,
      ...query
    } = _ref9;
    return this._paginateOverList(this.listUtterances, {
      kbId,
      limit,
      ...query
    });
  }
  async listAllPatterns(_ref10) {
    let {
      kbId,
      limit,
      ...query
    } = _ref10;
    return this._paginateOverList(this.listPatterns, {
      kbId,
      limit,
      ...query
    });
  }
  async getPrebuiltDomains(_ref11) {
    let {
      kbId
    } = _ref11;
    const headers = await this.getHeaders();
    return _axios.default.get(`${this.nluApiUrl}/knowledge-bases/${kbId}/prebuilt-models`, {
      ...headers
    }).then(response => response.data);
  }
  async listIntents(_ref12) {
    let {
      kbId,
      appId,
      last,
      limit,
      name,
      label,
      ...query
    } = _ref12;
    const headers = await this.getHeaders();
    return _axios.default.get(`${this.nluApiUrl}/intents`, {
      ...headers,
      params: {
        kbId,
        appId,
        last,
        limit,
        name,
        label,
        ...query
      }
    }).then(response => response.data);
  }

  // TODO: delete it(for internal use)
  async createPrebuiltModel(body) {
    const headers = await this.getHeaders();
    return _axios.default.post(`${this.nluApiUrl}/prebuilt-models`, body, headers).then(response => response.data);
  }
  async getQueryReporting(_ref13) {
    let {
      kbId,
      reportingType,
      sort,
      order,
      limit,
      last
    } = _ref13;
    const headers = await this.getHeaders();
    return _axios.default.get(`${this.nluApiUrl}/reporting/queries`, {
      ...headers,
      params: {
        kbId,
        reportingType,
        sort,
        order,
        limit,
        last
      }
    }).then(response => response.data);
  }
  async removePrebuiltModelFromKM(_ref14) {
    let {
      prebuiltModelId,
      kbId
    } = _ref14;
    const headers = await this.getHeaders();
    return _axios.default.delete(`${this.nluApiUrl}/knowledge-base/prebuilt-model`, {
      ...headers,
      params: {
        prebuiltModelId,
        kbId
      }
    }).then(response => response.data);
  }
  async addPrebuiltModelToKM(_ref15) {
    let {
      kbId,
      prebuiltModelId,
      appId,
      appName,
      shouldCreateNewApplication,
      responses,
      entities
    } = _ref15;
    const headers = await this.getHeaders();
    return _axios.default.post(`${this.nluApiUrl}/knowledge-base/prebuilt-model`, {
      kbId,
      appId,
      prebuiltModelId,
      appName,
      shouldCreateNewApplication,
      responses,
      entities
    }, headers).then(response => response.data);
  }
  async getPrebuiltModelDetails(_ref16) {
    let {
      kbId,
      prebuiltModelId
    } = _ref16;
    const headers = await this.getHeaders();
    return _axios.default.get(`${this.nluApiUrl}/knowledge-base/prebuilt-model`, {
      ...headers,
      params: {
        kbId,
        prebuiltModelId
      }
    }).then(response => response.data);
  }
  async createKnowledgeBase(_ref17) {
    let {
      name,
      desc,
      imageSrc,
      config,
      culture,
      extensions,
      multilingual
    } = _ref17;
    const headers = await this.getHeaders();
    return _axios.default.post(`${this.nluApiUrl}/knowledge-base`, {
      name,
      desc,
      imageSrc,
      config,
      culture,
      extensions,
      multilingual
    }, headers).then(response => response.data);
  }
  async createApplication(_ref18) {
    let {
      kbId,
      name,
      desc,
      culture,
      model_features
    } = _ref18;
    const headers = await this.getHeaders();
    return _axios.default.post(`${this.nluApiUrl}/applications`, {
      kbId,
      name,
      desc,
      culture,
      model_features
    }, headers).then(response => response.data);
  }
  async createIntent(_ref19) {
    let {
      name,
      type,
      kbId,
      appId,
      label,
      postprocessFlows,
      customData,
      features
    } = _ref19;
    const headers = await this.getHeaders();
    return _axios.default.post(`${this.nluApiUrl}/intents`, {
      name,
      type,
      postprocessFlows,
      customData,
      features,
      label
    }, {
      ...headers,
      params: {
        kbId,
        appId
      }
    }).then(response => response.data);
  }
  async createUtterance(_ref20) {
    let {
      appId,
      kbId,
      autoGenerated,
      updatedCache,
      text,
      intent,
      entities
    } = _ref20;
    const headers = await this.getHeaders();
    return _axios.default.post(`${this.nluApiUrl}/utterances`, {
      autoGenerated,
      updatedCache,
      text,
      intent,
      entities
    }, {
      ...headers,
      params: {
        kbId,
        appId
      }
    }).then(response => response.data);
  }
  async batchCreateUtterances(_ref21) {
    let {
      appId,
      kbId,
      utterances
    } = _ref21;
    const headers = await this.getHeaders();
    return _axios.default.post(`${this.nluApiUrl}/utterances/bulk`, utterances, {
      ...headers,
      params: {
        kbId,
        appId
      }
    }).then(response => response.data);
  }
  async createPattern(_ref22) {
    let {
      appId,
      kbId,
      pattern,
      intent,
      entities
    } = _ref22;
    const headers = await this.getHeaders();
    return _axios.default.post(`${this.nluApiUrl}/patterns`, {
      pattern,
      intent,
      entities
    }, {
      ...headers,
      params: {
        kbId,
        appId
      }
    }).then(response => response.data);
  }
  async createPair(_ref23) {
    let {
      kbId,
      applicationName,
      intentName,
      intentType,
      utteranceText
    } = _ref23;
    const headers = await this.getHeaders();
    return _axios.default.post(`${this.nluApiUrl}/knowledge-base/pair`, {
      kbId,
      applicationName,
      intentName,
      intentType,
      utteranceText
    }, headers).then(response => response.data);
  }
  async batchCreatePairs(_ref24) {
    let {
      kbId,
      utterances
    } = _ref24;
    const headers = await this.getHeaders();
    return _axios.default.post(`${this.nluApiUrl}/knowledge-base/pairs`, {
      kbId,
      utterances
    }, headers).then(response => response.data);
  }
  async batchCreatePairsFile(_ref25) {
    let {
      kbId,
      fileUrl
    } = _ref25;
    const headers = await this.getHeaders();
    return _axios.default.post(`${this.nluApiUrl}/knowledge-base/pairs/file`, {
      kbId,
      fileUrl
    }, headers).then(response => response.data);
  }
  async createPatternPair(_ref26) {
    let {
      kbId,
      applicationName,
      intentName,
      intentType,
      patternText,
      patternEntities
    } = _ref26;
    const headers = await this.getHeaders();
    return _axios.default.post(`${this.nluApiUrl}/knowledge-base/patterns/pair`, {
      kbId,
      applicationName,
      intentName,
      intentType,
      patternText,
      patternEntities
    }, headers).then(response => response.data);
  }
  async createPairsCsv(_ref27) {
    let {
      kbId,
      fileUrl,
      applicationName,
      delimiter
    } = _ref27;
    const headers = await this.getHeaders();
    return _axios.default.post(`${this.nluApiUrl}/knowledge-base/pair/csv`, {
      kbId,
      applicationName,
      fileUrl,
      delimiter
    }, headers).then(response => response.data);
  }

  /**
   * Create a phrase with optional language option [v2 api]
   *
   * @param {Object} params
   * @param {string} params.knowledgeModelId
   * @param {string} params.contextId
   * @param {string} params.intent
   * @param {string} params.text
   * @param {string} [params.culture]
   *
   * @returns {Promise<UtteranceCreateResult>}
  */

  async createUtteranceV2(_ref28) {
    let {
      knowledgeModelId,
      contextId,
      responseId,
      phrase,
      culture
    } = _ref28;
    const headers = await this.getHeaders();
    const payload = {
      text: phrase,
      intent: responseId
    };
    if (culture) Object.assign(payload, {
      culture
    });
    return _axios.default.post(`${this.nluApiUrl}/knowledge-bases/${knowledgeModelId}/contexts/${contextId}/utterances`, payload, headers).then(response => response.data);
  }

  /**
  * Create multiple phrases at once on a given language (if specified) [v2 api]
  *
  * @param {Object} params
  * @param {string} params.knowledgeModelId
  * @param {string} params.contextId
  * @param {string} params.intentId
  * @param {string[]} params.utterances
  * @param {string} [params.culture] - lang code, only for models supporting multilanguage
  *
  * @returns {Promise<Array.<UtterancesBulkCreateResult>>}
  */
  async createUtterancesBulk(_ref29) {
    let {
      knowledgeModelId,
      contextId,
      intentId,
      utterances,
      culture
    } = _ref29;
    const headers = await this.getHeaders();
    const payload = {
      intent: intentId,
      utterances
    };
    if (culture) Object.assign(payload, {
      culture
    });
    return _axios.default.post(`${this.nluApiUrl}/knowledge-bases/${knowledgeModelId}/contexts/${contextId}/utterances/bulk`, payload, headers).then(response => response.data);
  }
  async createEntity(_ref30) {
    let {
      type,
      kbId,
      name,
      roles,
      ...rest
    } = _ref30;
    const headers = await this.getHeaders();
    return _axios.default.post(`${this.nluApiUrl}/kb/entities/${type}`, NluApi._getEntityBody(type, {
      name,
      roles,
      ...rest
    }), {
      ...headers,
      params: {
        kbId
      }
    }).then(response => response.data);
  }
  async createFeature(_ref31) {
    let {
      kbId,
      name,
      mode,
      words,
      enabledForAllModels
    } = _ref31;
    const headers = await this.getHeaders();
    return _axios.default.post(`${this.nluApiUrl}/phraselists`, {
      name,
      mode,
      words,
      enabledForAllModels
    }, {
      ...headers,
      params: {
        kbId
      }
    }).then(response => response.data);
  }
  async updateKnowledgeBase(knowledgeBase) {
    const headers = await this.getHeaders();
    return _axios.default.put(`${this.nluApiUrl}/knowledge-base`, knowledgeBase, headers).then(response => response.data);
  }
  async updateApplication(_ref32) {
    let {
      kbId,
      id,
      name,
      desc,
      model_features
    } = _ref32;
    const headers = await this.getHeaders();
    return _axios.default.put(`${this.nluApiUrl}/applications`, {
      kbId,
      id,
      name,
      desc,
      model_features
    }, headers).then(response => response.data);
  }
  async updateIntent(_ref33) {
    let {
      name,
      type,
      id,
      appId,
      kbId,
      postprocessFlows,
      customData,
      features,
      label
    } = _ref33;
    const headers = await this.getHeaders();
    return _axios.default.put(`${this.nluApiUrl}/intents`, {
      name,
      type,
      postprocessFlows,
      customData,
      features,
      label
    }, {
      ...headers,
      params: {
        id,
        kbId,
        appId
      }
    }).then(response => response.data);
  }
  async updateUtterance(_ref34) {
    let {
      appId,
      kbId,
      utterance
    } = _ref34;
    const headers = await this.getHeaders();
    return _axios.default.put(`${this.nluApiUrl}/utterances`, utterance, {
      ...headers,
      params: {
        kbId,
        appId
      }
    }).then(response => response.data);
  }

  /**
   * Update phrase, including text, context, response and language. [v2 api]
   *
   * @param {Object} params
   * @param {string} params.knowledgeModelId
   * @param {string} params.contextId - source contextId
   * @param {string} params.phraseId
   * @param {Object} params.data - properties updated
   * @param {string} params.data.text
   * @param {string} [params.data.intent] - new responseId
   * @param {string} [params.data.application] - new contextId
   * @param {string} [params.data.culture] - new lang code, only for models supporting multilanguage
   *
   * @returns {Promise<UtteranceUpdateResult>}
   */
  async updateUtteranceV2(_ref35) {
    let {
      knowledgeModelId,
      contextId,
      phraseId,
      data
    } = _ref35;
    const headers = await this.getHeaders();
    return _axios.default.put(`${this.nluApiUrl}/knowledge-bases/${knowledgeModelId}/contexts/${contextId}/utterances/${phraseId}`, data, headers).then(response => response.data);
  }
  async updatePattern(_ref36) {
    let {
      appId,
      kbId,
      pattern
    } = _ref36;
    const headers = await this.getHeaders();
    return _axios.default.put(`${this.nluApiUrl}/patterns`, pattern, {
      ...headers,
      params: {
        kbId,
        appId
      }
    }).then(response => response.data);
  }
  async updatePair(_ref37) {
    let {
      kbId,
      applicationName,
      intentName,
      intentType,
      utterance
    } = _ref37;
    const headers = await this.getHeaders();
    return _axios.default.put(`${this.nluApiUrl}/knowledge-base/pair`, {
      kbId,
      applicationName,
      intentName,
      intentType,
      utterance
    }, headers).then(response => response.data);
  }
  async batchUpdatePairs(_ref38) {
    let {
      kbId,
      utterances
    } = _ref38;
    const headers = await this.getHeaders();
    return _axios.default.put(`${this.nluApiUrl}/knowledge-base/pairs`, utterances, {
      ...headers,
      params: {
        kbId
      }
    }).then(response => response.data);
  }
  async updateEntity(_ref39) {
    let {
      type,
      id,
      kbId,
      name,
      roles,
      ...rest
    } = _ref39;
    const headers = await this.getHeaders();
    return _axios.default.put(`${this.nluApiUrl}/kb/entities/${type}`, NluApi._getEntityBody(type, {
      name,
      roles,
      ...rest
    }), {
      ...headers,
      params: {
        id,
        kbId
      }
    }).then(response => response.data);
  }
  async updateFeature(_ref40) {
    let {
      kbId,
      id,
      name,
      mode,
      words,
      enabledForAllModels,
      activated = true
    } = _ref40;
    const headers = await this.getHeaders();
    return _axios.default.put(`${this.nluApiUrl}/phraselists`, {
      name,
      mode,
      words,
      enabledForAllModels,
      activated
    }, {
      ...headers,
      params: {
        kbId,
        id
      }
    }).then(response => response.data);
  }
  async updatePrebuiltEntities(_ref41) {
    let {
      kbId,
      list
    } = _ref41;
    const headers = await this.getHeaders();
    return _axios.default.post(`${this.nluApiUrl}/kb/entities/prebuilt`, list, {
      ...headers,
      params: {
        kbId
      }
    }).then(response => response.data);
  }
  async deleteKnowledgeBase(_ref42) {
    let {
      id
    } = _ref42;
    const headers = await this.getHeaders();
    return _axios.default.delete(`${this.nluApiUrl}/knowledge-base`, {
      ...headers,
      params: {
        id
      }
    }).then(response => response.data);
  }
  async deleteApplication(_ref43) {
    let {
      kbId,
      appId
    } = _ref43;
    const headers = await this.getHeaders();
    return _axios.default.delete(`${this.nluApiUrl}/applications`, {
      ...headers,
      params: {
        kbId,
        appId
      }
    }).then(response => response.data);
  }
  async deleteIntent(_ref44) {
    let {
      id,
      appId,
      kbId
    } = _ref44;
    const headers = await this.getHeaders();
    return _axios.default.delete(`${this.nluApiUrl}/intents`, {
      ...headers,
      params: {
        id,
        kbId,
        appId,
        deleteUtterances: true
      }
    }).then(response => response.data);
  }
  async deleteUtterance(_ref45) {
    let {
      id,
      kbId
    } = _ref45;
    const headers = await this.getHeaders();
    return _axios.default.delete(`${this.nluApiUrl}/utterances`, {
      ...headers,
      params: {
        id,
        kbId
      }
    }).then(response => response.data);
  }
  async batchDeleteUtterances(_ref46) {
    let {
      ids,
      kbId
    } = _ref46;
    const headers = await this.getHeaders();
    return _axios.default.post(`${this.nluApiUrl}/utterances/bulk/delete`, ids, {
      ...headers,
      params: {
        kbId
      }
    }).then(response => response.data);
  }
  async deletePattern(_ref47) {
    let {
      id,
      kbId
    } = _ref47;
    const headers = await this.getHeaders();
    return _axios.default.delete(`${this.nluApiUrl}/patterns`, {
      ...headers,
      params: {
        id,
        kbId
      }
    }).then(response => response.data);
  }
  async deleteEntity(_ref48) {
    let {
      type,
      id,
      kbId
    } = _ref48;
    const headers = await this.getHeaders();
    return _axios.default.delete(`${this.nluApiUrl}/kb/entities/${type}`, {
      ...headers,
      params: {
        id,
        kbId
      }
    }).then(response => response.data);
  }
  async deleteFeature(_ref49) {
    let {
      id,
      kbId
    } = _ref49;
    const headers = await this.getHeaders();
    return _axios.default.delete(`${this.nluApiUrl}/phraselists`, {
      ...headers,
      params: {
        id,
        kbId
      }
    }).then(response => response.data);
  }
  async getTrainStatus(_ref50) {
    let {
      kbId,
      versionId = null,
      signal = null
    } = _ref50;
    const headers = await this.getHeaders();
    return _axios.default.get(`${this.nluApiUrl}/train/status`, {
      ...headers,
      params: {
        kbId,
        versionId
      },
      signal
    }).then(response => response.data);
  }
  async train(_ref51) {
    let {
      kbId,
      flowId,
      phraseId,
      sessionId,
      autoPublish
    } = _ref51;
    const headers = await this.getHeaders();
    return _axios.default.post(`${this.nluApiUrl}/kb/train`, null, {
      ...headers,
      params: {
        kbId,
        flowId,
        phraseId,
        sessionId,
        autoPublish
      }
    }).then(response => response.data);
  }
  async trainForTest(_ref52) {
    let {
      kbId,
      flowId,
      phraseId,
      sessionId
    } = _ref52;
    const headers = await this.getHeaders();
    return _axios.default.post(`${this.nluApiUrl}/kb/train/test`, null, {
      ...headers,
      params: {
        kbId,
        flowId,
        phraseId,
        sessionId
      }
    }).then(response => response.data);
  }
  async publish(_ref53) {
    let {
      kbId,
      versionId = null
    } = _ref53;
    const headers = await this.getHeaders();
    return _axios.default.post(`${this.nluApiUrl}/kb/publish`, null, {
      ...headers,
      params: {
        kbId,
        versionId
      }
    }).then(response => response.data);
  }
  async recover(_ref54) {
    let {
      kbId,
      versionId
    } = _ref54;
    const headers = await this.getHeaders();
    return _axios.default.post(`${this.nluApiUrl}/kb/recover`, null, {
      ...headers,
      params: {
        kbId,
        versionId
      }
    }).then(response => response.data);
  }
  async deleteVersion(_ref55) {
    let {
      kbId,
      versionId
    } = _ref55;
    const headers = await this.getHeaders();
    return _axios.default.delete(`${this.nluApiUrl}/knowledge-bases/${kbId}/versions/${versionId}`, {
      ...headers
    }).then(response => response.data);
  }
  async batchDeleteVersions(_ref56) {
    let {
      kbId,
      versionIds
    } = _ref56;
    const headers = await this.getHeaders();
    return _axios.default.delete(`${this.nluApiUrl}/knowledge-bases/${kbId}/versions`, {
      data: versionIds,
      ...headers
    }).then(response => response.data);
  }
  async updateVersion(_ref57) {
    let {
      kbId,
      versionId,
      name
    } = _ref57;
    const headers = await this.getHeaders();
    return _axios.default.put(`${this.nluApiUrl}/knowledge-bases/${kbId}/versions/${versionId}`, {
      name
    }, {
      ...headers
    }).then(response => response.data);
  }
  async clone(_ref58) {
    let {
      kbId,
      newName
    } = _ref58;
    const headers = await this.getHeaders();
    return _axios.default.post(`${this.nluApiUrl}/knowledge-base/clone`, {
      newName
    }, {
      ...headers,
      params: {
        id: kbId
      }
    }).then(response => response.data);
  }
  async migrateLuisToClu(_ref59) {
    let {
      kbId
    } = _ref59;
    const headers = await this.getHeaders();
    return _axios.default.post(`${this.nluApiUrl}/knowledge-base/${kbId}/migrate-luis-to-clu`, null, headers).then(response => response.data);
  }
  async export(_ref60) {
    let {
      kbId,
      isPublic,
      exclude
    } = _ref60;
    const headers = await this.getHeaders();
    return _axios.default.get(`${this.nluApiUrl}/knowledge-base/export`, {
      ...headers,
      params: {
        id: kbId,
        isPublic,
        exclude
      }
    }).then(response => response.data);
  }
  async import(_ref61) {
    let {
      fileUrl,
      knowledgeBase
    } = _ref61;
    const headers = await this.getHeaders();
    return _axios.default.post(`${this.nluApiUrl}/knowledge-base/import`, {
      fileUrl,
      knowledgeBase
    }, headers).then(response => response.data);
  }
  async importJson(data) {
    const headers = await this.getHeaders();
    return _axios.default.post(`${this.nluApiUrl}/knowledge-base/import/json`, data, headers).then(response => response.data);
  }
  async importCsv(_ref62) {
    let {
      fileUrl,
      knowledgeBase,
      applicationName,
      delimiter
    } = _ref62;
    const headers = await this.getHeaders();
    return _axios.default.post(`${this.nluApiUrl}/knowledge-base/import/csv`, {
      fileUrl,
      knowledgeBase,
      applicationName,
      delimiter
    }, headers).then(response => response.data);
  }
  async getProvisioningStatus(_ref63) {
    let {
      appId,
      kbId
    } = _ref63;
    const headers = await this.getHeaders();
    return _axios.default.get(`${this.nluApiUrl}/provisioning/status`, {
      ...headers,
      params: {
        kbId,
        appId
      }
    }).then(response => response.data);
  }
  async updateProvisioningStatus(_ref64) {
    let {
      accountId,
      applicationId,
      status,
      kbId,
      isKb
    } = _ref64;
    const headers = await this.getHeaders();
    return _axios.default.put(`${this.nluApiUrl}/provisioning/status`, {
      accountId,
      applicationId,
      status,
      kbId,
      isKb
    }, headers).then(response => response.data);
  }
  async meaning(data) {
    const headers = await this.getHeaders();
    return _axios.default.post(`${this.nluApiUrl}/meaning`, data, headers).then(response => response.data);
  }
  async createContext(data) {
    const headers = await this.getHeaders();
    return _axios.default.post(`${this.nluApiUrl}/applications`, data, headers).then(response => response.data);
  }
  async mergeContexts(_ref65) {
    let {
      kbId,
      targetContext,
      mergingContextsIds
    } = _ref65;
    const headers = await this.getHeaders();
    return _axios.default.post(`${this.nluApiUrl}/applications/merge`, {
      kbId,
      targetContext,
      mergingContextsIds
    }, headers).then(response => response.data);
  }
  async mergeIntents(_ref66) {
    let {
      kbId,
      appId,
      targetIntent,
      mergingIntentsIds
    } = _ref66;
    const headers = await this.getHeaders();
    return _axios.default.post(`${this.nluApiUrl}/intents/merge`, {
      kbId,
      appId,
      targetIntent,
      mergingIntentsIds
    }, headers).then(response => response.data);
  }
  async testMeaning(_ref67) {
    let {
      q,
      appId,
      kbId
    } = _ref67;
    const headers = await this.getHeaders();
    return _axios.default.post(`${this.nluApiUrl}/meaning/test`, {
      q,
      appId,
      kbId
    }, headers).then(response => response.data);
  }
  async qnaMeaning(_ref68) {
    let {
      q,
      appId,
      kbId,
      isKb
    } = _ref68;
    const headers = await this.getHeaders();
    return _axios.default.post(`${this.nluApiUrl}/qna/meaning`, {
      q,
      appId,
      kbId,
      isKb
    }, headers).then(response => response.data);
  }
  async getLogs(_ref69) {
    let {
      kbId,
      search,
      start,
      end,
      last
    } = _ref69;
    const headers = await this.getHeaders();
    return _axios.default.get(`${this.nluApiUrl}/events`, {
      ...headers,
      params: {
        kbId,
        search,
        start,
        end,
        last
      }
    }).then(response => response.data);
  }
  async deleteLogs(_ref70) {
    let {
      kbId,
      search,
      start,
      end
    } = _ref70;
    const headers = await this.getHeaders();
    return _axios.default.delete(`${this.nluApiUrl}/events`, {
      ...headers,
      params: {
        kbId,
        search,
        start,
        end
      }
    }).then(response => response.data);
  }

  /**
   * Get a list of composite entities
   *
   * @param {Object} params
   * @param {string} params.kbId
   * @param {string} [params.versionId]
   * @param {string} [params.last]
   * @param {number} [params.limit]
   * @param {CompositeEntityType} [params.type]
   *
   * @returns {Promise<NluResponse<CompositeEntity>>}
   */
  async listCompositeEntities() {
    let {
      kbId,
      versionId,
      last,
      limit,
      type
    } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    const headers = await this.getHeaders();
    return _axios.default.get(`${this.nluApiUrl}/knowledge-bases/${kbId}/entities/composite`, {
      ...headers,
      params: {
        versionId,
        last,
        limit,
        type
      }
    }).then(response => response.data);
  }

  /**
   * Create a composite entity
   * @param {CompositeEntityCreateProps} data
   *
   * @returns {Promise<CompositeEntity>}
   */
  async createCompositeEntity(_ref71) {
    let {
      kbId,
      ...entityData
    } = _ref71;
    const headers = await this.getHeaders();
    return _axios.default.post(`${this.nluApiUrl}/knowledge-bases/${kbId}/entities/composite`, entityData, headers).then(response => response.data);
  }

  /**
   * Update a composite entity
   *
   * @param {CompositeEntityUpdateProps} data
   *
   * @returns {Promise<CompositeEntity>}
   */
  async updateCompositeEntity(_ref72) {
    let {
      kbId,
      id,
      ...entityData
    } = _ref72;
    const headers = await this.getHeaders();
    return _axios.default.put(`${this.nluApiUrl}/knowledge-bases/${kbId}/entities/composite/${id}`, entityData, headers).then(response => response.data);
  }

  /**
   * Delete a composite entity
   *
   * @param {Object} params
   * @param {string} params.kbId
   * @param {string} params.id CompositeEntity ID
   *
   * @returns {Promise<CompositeEntityDeleteResult>}
   */
  async deleteCompositeEntity(_ref73) {
    let {
      kbId,
      id
    } = _ref73;
    const headers = await this.getHeaders();
    return _axios.default.delete(`${this.nluApiUrl}/knowledge-bases/${kbId}/entities/composite/${id}`, {
      ...headers
    }).then(response => response.data);
  }
  static _getEntityBody(type, data) {
    const base = {
      name: data.name,
      roles: data.roles
    };
    switch (type) {
      case 'simple':
        return {
          ...base,
          features: data.features,
          children: data.children
        };
      case 'regex':
        return {
          ...base,
          regexPattern: data.regexPattern
        };
      case 'prebuilt':
        return base;
      case 'postprocess':
        return {
          name: data.name,
          processFlow: data.processFlow
        };
      case 'list':
        return {
          ...base,
          subLists: data.subLists
        };
      case 'patternany':
        return base;
      default:
        throw new Error(`Unknown entity type ${type}`);
    }
  }
}
var _default = NluApi;
exports.default = _default;